import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Receipt, DollarSign, Clock, CheckCircle, Trash2, CalendarIcon, X } from 'lucide-react';
import { formatCurrency } from '@/lib/currency';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useExpenses, useExpenseStats } from '@/hooks/finance/useExpenses';
import { useDeleteExpense } from '@/hooks/finance/useDeleteExpense';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useExpenseCategories } from '@/hooks/finance/useExpenseCategories';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { format, parseISO, isBefore, isAfter, startOfDay, endOfDay } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/finance/DeleteConfirmationDialog';
import { useFinancePagination } from '@/hooks/use-finance-pagination';
import FinancePagination from '@/components/finance/FinancePagination';
import { cn } from '@/lib/utils';
import { BusinessUnitSelect, BusinessUnitBadge } from '@/components/finance/BusinessUnitSelect';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';

const ExpensesPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [expenseToDelete, setExpenseToDelete] = React.useState<{ id: string; number: string } | null>(null);

  const isAdmin = userRole === 'admin';

  // URL-persisted state
  const activeTab = searchParams.get('tab') || 'all';
  const searchTerm = searchParams.get('search') || '';
  const selectedDepartment = searchParams.get('dept') || 'all';
  const selectedCategory = searchParams.get('cat') || 'all';
  const selectedBank = searchParams.get('bank') || 'all';
  const dateFromStr = searchParams.get('from') || '';
  const dateToStr = searchParams.get('to') || '';
  const selectedBU = (searchParams.get('bu') || '') as BusinessUnit | '';

  const dateFrom = dateFromStr ? new Date(dateFromStr) : undefined;
  const dateTo = dateToStr ? new Date(dateToStr) : undefined;

  const updateParam = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all' || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const filters = {
    department_id: selectedDepartment !== 'all' ? selectedDepartment : undefined,
    category_id: selectedCategory !== 'all' ? selectedCategory : undefined,
    payment_status: activeTab !== 'all' ? activeTab : undefined,
    bank_account_id: selectedBank !== 'all' ? selectedBank : undefined,
    business_unit: selectedBU || undefined,
  };

  const { data: expenses, isLoading } = useExpenses(filters);
  const { data: stats } = useExpenseStats();
  const { data: departments } = useDepartments();
  const { data: categories } = useExpenseCategories();
  const { data: bankAccounts } = useBankAccounts(true);
  const deleteExpense = useDeleteExpense();

  const handleDeleteClick = (e: React.MouseEvent, id: string, expenseNumber: string) => {
    e.stopPropagation();
    setExpenseToDelete({ id, number: expenseNumber });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;
    await deleteExpense.mutateAsync(expenseToDelete.id);
    setDeleteDialogOpen(false);
    setExpenseToDelete(null);
  };

  const filteredExpenses = useMemo(() => {
    return expenses?.filter(exp => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = !term ||
        exp.description.toLowerCase().includes(term) ||
        exp.expense_number.toLowerCase().includes(term) ||
        exp.vendor_name?.toLowerCase().includes(term) ||
        exp.amount?.toString().includes(term) ||
        exp.total_amount?.toString().includes(term);

      let matchesDateFrom = true;
      let matchesDateTo = true;
      if (dateFrom) {
        matchesDateFrom = !isBefore(parseISO(exp.expense_date), startOfDay(dateFrom));
      }
      if (dateTo) {
        matchesDateTo = !isAfter(parseISO(exp.expense_date), endOfDay(dateTo));
      }

      return matchesSearch && matchesDateFrom && matchesDateTo;
    }) || [];
  }, [expenses, searchTerm, dateFrom, dateTo]);

  const pagination = useFinancePagination({ data: filteredExpenses, itemsPerPage: 10 });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Expenses</h1>
            <p className="text-muted-foreground">Track and manage company expenses</p>
          </div>
          <Button onClick={() => navigate('/finance/accounting/expenses/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Record Expense
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-xl font-bold">{formatCurrency(stats?.total || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Paid</p>
                  <p className="text-xl font-bold">{formatCurrency(stats?.paid || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-xl font-bold">{formatCurrency(stats?.pending || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <DollarSign className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="text-xl font-bold">{stats?.count || 0} records</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by description, number, vendor, or amount..."
                value={searchTerm}
                onChange={(e) => updateParam('search', e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedDepartment} onValueChange={(v) => updateParam('dept', v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={(v) => updateParam('cat', v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories?.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedBank} onValueChange={(v) => updateParam('bank', v)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Bank Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Banks</SelectItem>
                {bankAccounts?.map((acc) => (
                  <SelectItem key={acc.id} value={acc.id}>
                    {acc.name}{acc.bank_name ? ` (${acc.bank_name})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <BusinessUnitSelect
              value={selectedBU}
              includeAll
              allLabel="All Business Units"
              onValueChange={(v) => updateParam('bu', v as string)}
              className="w-full sm:w-[180px]"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Date From */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[180px] justify-start text-left font-normal",
                    !dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'MMM d, yyyy') : 'From date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={(d) => updateParam('from', d ? format(d, 'yyyy-MM-dd') : '')}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            {/* Date To */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full sm:w-[180px] justify-start text-left font-normal",
                    !dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'MMM d, yyyy') : 'To date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={(d) => updateParam('to', d ? format(d, 'yyyy-MM-dd') : '')}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            {/* Clear filters */}
            {(searchTerm || selectedDepartment !== 'all' || selectedCategory !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams(prev => {
                  const next = new URLSearchParams();
                  const tab = prev.get('tab');
                  if (tab) next.set('tab', tab);
                  return next;
                })}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {/* Tabs and List */}
        <Tabs value={activeTab} onValueChange={(v) => updateParam('tab', v)}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-1/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredExpenses.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No expenses found</h3>
                  <p className="text-muted-foreground mb-4">
                    Start recording your expenses to track spending
                  </p>
                  <Button onClick={() => navigate('/finance/accounting/expenses/new')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Record Expense
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pagination.paginatedData.map((expense) => (
                  <Card
                    key={expense.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/finance/accounting/expenses/${expense.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-muted-foreground">
                              {expense.expense_number}
                            </span>
                            <Badge variant={expense.payment_status === 'paid' ? 'default' : 'outline'}>
                              {expense.payment_status === 'paid' ? (
                                <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" /> Pending</>
                              )}
                            </Badge>
                            {(expense as any).business_unit === 'split' && (expense as any).bu_allocations?.length > 0 ? (
                              <div className="flex flex-wrap items-center gap-1.5">
                                {(expense as any).bu_allocations
                                  .slice()
                                  .sort((a: any, b: any) => Number(b.percentage) - Number(a.percentage))
                                  .map((a: any) => {
                                    const bu = BUSINESS_UNITS.find((b) => b.value === a.business_unit);
                                    return (
                                      <Badge
                                        key={a.business_unit}
                                        variant="outline"
                                        style={{ borderColor: bu?.color ?? 'hsl(var(--muted-foreground))', color: bu?.color ?? 'hsl(var(--muted-foreground))' }}
                                      >
                                        {bu?.label || a.business_unit}
                                        <span className="ml-1 opacity-70">{Number(a.percentage)}%</span>
                                      </Badge>
                                    );
                                  })}
                              </div>
                            ) : (
                              <BusinessUnitBadge value={(expense as any).business_unit} />
                            )}
                          </div>
                          <h3 className="font-medium">{expense.description}</h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {expense.category && <span>{expense.category.name}</span>}
                            {expense.vendor_name && <span>• {expense.vendor_name}</span>}
                            <span>• {format(new Date(expense.expense_date), 'MMM d, yyyy')}</span>
                          </div>
                        </div>
                        <div className="text-right flex items-start gap-2">
                          <div>
                            {expense.payment_status === 'paid' && expense.net_payment ? (
                              <>
                                <p className="text-lg font-semibold">{formatCurrency(expense.net_payment, expense.currency)}</p>
                                <p className="text-xs text-muted-foreground">Net Paid</p>
                              </>
                            ) : (
                              <p className="text-lg font-semibold">{formatCurrency(expense.total_amount, expense.currency)}</p>
                            )}
                            {expense.department && (
                              <p className="text-sm text-muted-foreground">{expense.department.name}</p>
                            )}
                          </div>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={(e) => handleDeleteClick(e, expense.id, expense.expense_number)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <FinancePagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.goToPage}
                  hasNextPage={pagination.hasNextPage}
                  hasPreviousPage={pagination.hasPreviousPage}
                  totalItems={pagination.totalItems}
                  startIndex={pagination.startIndex}
                  endIndex={pagination.endIndex}
                  itemsPerPage={pagination.itemsPerPage}
                  onItemsPerPageChange={pagination.changePageSize}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          isDeleting={deleteExpense.isPending}
          title="Delete Expense"
          description={`Are you sure you want to delete ${expenseToDelete?.number}? This action cannot be undone.`}
        />
      </div>
    </Layout>
  );
};

export default ExpensesPage;
